/**
 * IDD (Input Data Dictionary) Parser
 *
 * Parses tokenized IDD content into a structured schema that can be used
 * for validation, auto-completion, and documentation.
 */

import { IDDLexer } from './lexer';
import {
  type IDDSchema,
  type IDDObjectType,
  type IDDField,
  type IDDFieldType,
  type IDDToken,
  type IDDParseError,
  type IDDParseResult,
  createEmptySchema,
  createDefaultField,
  createDefaultObjectType,
} from './types';

export class IDDParser {
  private tokens: IDDToken[] = [];
  private position: number = 0;
  private errors: IDDParseError[] = [];
  private currentGroup: string = '';
  private schema: IDDSchema;

  constructor() {
    this.schema = createEmptySchema();
  }

  /**
   * Parse IDD content string into a schema
   */
  parse(content: string): IDDParseResult {
    const lexer = new IDDLexer(content);
    this.tokens = lexer.tokenize();
    this.position = 0;
    this.errors = [];
    this.currentGroup = '';
    this.schema = createEmptySchema();

    // Extract version from the first lines (usually in a comment)
    this.extractVersion(content);

    // Parse all object definitions
    while (!this.isAtEnd()) {
      try {
        this.parseTopLevel();
      } catch {
        // Recover from errors by skipping to next object
        this.recoverFromError();
      }
    }

    // Build reference list mappings after all objects are parsed
    this.buildReferenceMappings();

    return {
      schema: this.schema,
      errors: this.errors,
    };
  }

  /**
   * Extract version information from IDD header
   */
  private extractVersion(content: string): void {
    // Look for version in the header comments
    // Format: !IDD_Version 24.1.0
    // Or: !IDD_BUILD xxxxx
    const versionMatch = content.match(/!IDD_Version\s+(\d+\.\d+\.\d+)/i);
    if (versionMatch && versionMatch[1]) {
      this.schema.version = versionMatch[1];
    }

    const buildMatch = content.match(/!IDD_BUILD\s+(\S+)/i);
    if (buildMatch && buildMatch[1]) {
      this.schema.build = buildMatch[1];
    }
  }

  /**
   * Parse a top-level element (group header or object definition)
   */
  private parseTopLevel(): void {
    this.skipWhitespaceAndComments();

    if (this.isAtEnd()) return;

    const token = this.current();

    if (token.type === 'GroupHeader') {
      this.parseGroupHeader();
    } else if (token.type === 'ClassName') {
      this.parseObjectDefinition();
    } else {
      // Skip unexpected tokens
      this.advance();
    }
  }

  /**
   * Parse a group header (\group Name)
   */
  private parseGroupHeader(): void {
    const token = this.current();
    this.currentGroup = token.value;

    // Add group if it doesn't exist
    if (!this.schema.groups.find((g) => g.name === this.currentGroup)) {
      this.schema.groups.push({
        name: this.currentGroup,
        objectTypes: [],
      });
    }

    this.advance();
  }

  /**
   * Parse an object type definition
   */
  private parseObjectDefinition(): void {
    const classNameToken = this.current();
    const objectType = createDefaultObjectType(classNameToken.value);
    objectType.group = this.currentGroup;

    this.advance();
    this.skipWhitespaceAndNewlines();

    // Expect comma after class name
    if (this.check('Comma')) {
      this.advance();
    }

    // Parse object-level properties and fields
    this.parseObjectBody(objectType);

    // Add to schema
    this.schema.objectTypes.set(
      objectType.name.toLowerCase(),
      objectType
    );

    // Add to current group
    const group = this.schema.groups.find((g) => g.name === this.currentGroup);
    if (group) {
      group.objectTypes.push(objectType.name);
    }
  }

  /**
   * Parse the body of an object definition (properties and fields)
   *
   * IDD format: properties come AFTER the field ID and comma/semicolon.
   * The semicolon marks the last field, but properties for that field
   * still follow after the semicolon.
   */
  private parseObjectBody(objectType: IDDObjectType): void {
    let currentField: IDDField | null = null;
    let memoBuffer: string[] = [];

    while (this.position < this.tokens.length) {
      this.skipWhitespaceOnly();

      const token = this.current();

      // New field - save previous field first
      if (token.type === 'FieldId') {
        // Save previous field if any
        if (currentField) {
          currentField.memo = memoBuffer.join(' ').trim();
          objectType.fields.push(currentField);
          memoBuffer = [];
        } else if (memoBuffer.length > 0) {
          // Object-level memo (properties before first field)
          objectType.memo = memoBuffer.join(' ').trim();
          memoBuffer = [];
        }

        // Start new field
        currentField = createDefaultField(token.value);
        this.advance();

        // Skip whitespace and consume comma or semicolon after field ID
        this.skipWhitespaceOnly();
        if (this.check('Comma')) {
          this.advance();
        } else if (this.check('Semicolon')) {
          this.advance();
        }
        continue;
      }

      // Property (backslash followed by property name)
      if (token.type === 'Backslash') {
        this.advance();
        this.parseProperty(objectType, currentField, memoBuffer);
        continue;
      }

      // Comma (field separator - might appear without a field ID in some cases)
      if (token.type === 'Comma') {
        this.advance();
        continue;
      }

      // Semicolon marks end of field list
      if (token.type === 'Semicolon') {
        this.advance();
        continue;
      }

      // Skip whitespace and newlines
      if (token.type === 'Newline' || token.type === 'Whitespace') {
        this.advance();
        continue;
      }

      // Skip comments
      if (token.type === 'Comment') {
        this.advance();
        continue;
      }

      // New object or group definition starts - finalize and exit
      if (token.type === 'ClassName' || token.type === 'GroupHeader') {
        break;
      }

      // EOF - exit
      if (token.type === 'EOF') {
        break;
      }

      // Unknown token - skip
      this.advance();
    }

    // Finalize: save any pending field or object-level memo
    if (currentField) {
      currentField.memo = memoBuffer.join(' ').trim();
      objectType.fields.push(currentField);
    } else if (memoBuffer.length > 0) {
      objectType.memo = memoBuffer.join(' ').trim();
    }
  }

  /**
   * Parse a property (\property-name value)
   */
  private parseProperty(
    objectType: IDDObjectType,
    currentField: IDDField | null,
    memoBuffer: string[]
  ): void {
    this.skipWhitespaceOnly();

    if (!this.check('PropertyName')) {
      return;
    }

    const propertyName = this.current().value.toLowerCase();
    this.advance();

    // Get property value if present
    this.skipWhitespaceOnly();
    let propertyValue = '';
    if (this.check('PropertyValue')) {
      propertyValue = this.current().value;
      this.advance();
    }

    // Apply property to field or object
    if (currentField) {
      this.applyFieldProperty(currentField, propertyName, propertyValue, memoBuffer);
    } else {
      this.applyObjectProperty(objectType, propertyName, propertyValue, memoBuffer);
    }
  }

  /**
   * Apply a property to an object type
   */
  private applyObjectProperty(
    objectType: IDDObjectType,
    propertyName: string,
    propertyValue: string,
    memoBuffer: string[]
  ): void {
    // Handle extensible:N format where number is part of property name
    if (propertyName.startsWith('extensible')) {
      const match = propertyName.match(/extensible:?(\d+)?/);
      if (match) {
        // Number can be in property name (extensible:1) or value
        const numFromName = match[1] ? parseInt(match[1], 10) : 0;
        const numFromValue = propertyValue ? parseInt(propertyValue, 10) : 0;
        objectType.extensible = numFromName || numFromValue || 0;
      }
      return;
    }

    switch (propertyName) {
      case 'memo':
      case 'note':
        memoBuffer.push(propertyValue);
        break;
      case 'unique-object':
        objectType.isUnique = true;
        break;
      case 'required-object':
        objectType.isRequired = true;
        break;
      case 'obsolete':
        objectType.isObsolete = true;
        break;
      case 'min-fields':
        objectType.minFields = parseInt(propertyValue, 10) || 0;
        break;
      case 'format':
        objectType.format = propertyValue;
        break;
    }
  }

  /**
   * Apply a property to a field
   */
  private applyFieldProperty(
    field: IDDField,
    propertyName: string,
    propertyValue: string,
    memoBuffer: string[]
  ): void {
    switch (propertyName) {
      case 'field':
        field.name = propertyValue;
        break;
      case 'memo':
      case 'note':
        memoBuffer.push(propertyValue);
        break;
      case 'required-field':
        field.required = true;
        break;
      case 'begin-extensible':
        field.beginExtensible = true;
        break;
      case 'type':
        field.type = this.parseFieldType(propertyValue);
        break;
      case 'key':
        if (!field.choices) {
          field.choices = [];
        }
        field.choices.push(propertyValue);
        break;
      case 'object-list':
        field.objectList = propertyValue;
        field.type = 'object-list';
        break;
      case 'external-list':
        field.type = 'external-list';
        field.externalList = propertyValue;
        break;
      case 'reference':
        if (!field.references) {
          field.references = [];
        }
        field.references.push(propertyValue);
        break;
      case 'reference-class-name':
        if (!field.references) {
          field.references = [];
        }
        field.references.push(propertyValue);
        break;
      case 'units':
        field.units = propertyValue;
        break;
      case 'ip-units':
        field.ipUnits = propertyValue;
        break;
      case 'unitsbasedonfield':
        field.unitsBasedOnField = propertyValue;
        break;
      case 'minimum':
        field.minimum = parseFloat(propertyValue);
        field.exclusiveMinimum = false;
        break;
      case 'minimum>':
        field.minimum = parseFloat(propertyValue);
        field.exclusiveMinimum = true;
        break;
      case 'maximum':
        field.maximum = parseFloat(propertyValue);
        field.exclusiveMaximum = false;
        break;
      case 'maximum<':
        field.maximum = parseFloat(propertyValue);
        field.exclusiveMaximum = true;
        break;
      case 'default':
        field.default = propertyValue;
        break;
      case 'autosizable':
        field.autosizable = true;
        break;
      case 'autocalculatable':
        field.autocalculatable = true;
        break;
      case 'deprecated':
        field.deprecated = true;
        break;
      case 'retaincase':
        field.retaincase = true;
        break;
    }
  }

  /**
   * Parse a field type string
   */
  private parseFieldType(value: string): IDDFieldType {
    const normalized = value.toLowerCase().trim();
    switch (normalized) {
      case 'real':
        return 'real';
      case 'integer':
        return 'integer';
      case 'alpha':
        return 'alpha';
      case 'choice':
        return 'choice';
      case 'object-list':
        return 'object-list';
      case 'external-list':
        return 'external-list';
      case 'node':
        return 'node';
      default:
        return 'alpha';
    }
  }

  /**
   * Build reference list mappings after all objects are parsed
   */
  private buildReferenceMappings(): void {
    // Build reference lists: which objects can be referenced by which list names
    for (const [, objectType] of this.schema.objectTypes) {
      for (const field of objectType.fields) {
        // Add to reference lists
        if (field.references) {
          for (const refList of field.references) {
            if (!this.schema.referenceLists.has(refList)) {
              this.schema.referenceLists.set(refList, new Set());
            }
            const refSet = this.schema.referenceLists.get(refList);
            if (refSet) {
              refSet.add(objectType.name);
            }
          }
        }

        // Track object-list usage
        if (field.objectList) {
          if (!this.schema.objectListMappings.has(field.objectList)) {
            this.schema.objectListMappings.set(field.objectList, []);
          }
          const mappings = this.schema.objectListMappings.get(field.objectList);
          if (mappings) {
            mappings.push(`${objectType.name}.${field.name}`);
          }
        }
      }
    }
  }

  /**
   * Recover from a parsing error by skipping to the next object
   */
  private recoverFromError(): void {
    while (!this.isAtEnd()) {
      const token = this.current();
      if (token.type === 'GroupHeader' || token.type === 'ClassName') {
        // Found start of next definition
        break;
      }
      if (token.type === 'Semicolon') {
        // End of current object
        this.advance();
        break;
      }
      this.advance();
    }
  }

  /**
   * Skip whitespace and comment tokens
   */
  private skipWhitespaceAndComments(): void {
    while (!this.isAtEnd()) {
      const token = this.current();
      if (
        token.type === 'Whitespace' ||
        token.type === 'Newline' ||
        token.type === 'Comment'
      ) {
        this.advance();
      } else {
        break;
      }
    }
  }

  /**
   * Skip only whitespace tokens (not newlines)
   */
  private skipWhitespaceOnly(): void {
    while (!this.isAtEnd()) {
      const token = this.current();
      if (token.type === 'Whitespace') {
        this.advance();
      } else {
        break;
      }
    }
  }

  /**
   * Skip whitespace and newline tokens
   */
  private skipWhitespaceAndNewlines(): void {
    while (!this.isAtEnd()) {
      const token = this.current();
      if (token.type === 'Whitespace' || token.type === 'Newline') {
        this.advance();
      } else {
        break;
      }
    }
  }

  /**
   * Get the current token
   */
  private current(): IDDToken {
    const token = this.tokens[this.position];
    // Return EOF token if out of bounds
    if (!token) {
      return { type: 'EOF', value: '', line: 0, column: 0 };
    }
    return token;
  }

  /**
   * Check if current token matches a type
   */
  private check(type: IDDToken['type']): boolean {
    if (this.isAtEnd()) return false;
    return this.current().type === type;
  }

  /**
   * Advance to the next token
   */
  private advance(): IDDToken {
    if (!this.isAtEnd()) {
      this.position++;
    }
    const token = this.tokens[this.position - 1];
    // Return EOF token if out of bounds
    if (!token) {
      return { type: 'EOF', value: '', line: 0, column: 0 };
    }
    return token;
  }

  /**
   * Check if we've reached the end of tokens
   */
  private isAtEnd(): boolean {
    return (
      this.position >= this.tokens.length ||
      this.current().type === 'EOF'
    );
  }
}

/**
 * Convenience function to parse IDD content
 */
export function parseIDD(content: string): IDDParseResult {
  const parser = new IDDParser();
  return parser.parse(content);
}
